import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { ScrollArea } from "./ui/scroll-area";
import {
  Rocket, ChevronDown, ChevronRight, Shield, Scale, Megaphone, FlaskConical,
  AlertTriangle, Settings2, Presentation, CheckCircle2, FileSignature, Zap,
  HeartPulse, Plus, Trash2, GripVertical, CalendarDays, Users, Save, Loader2,
  Target, Lock, BarChart3, CircleDot, Clock, ArrowUp, ArrowDown
} from "lucide-react";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_OPTIONS = [
  "Planning", "In Progress", "At Risk", "Blocked", "Ready for Go/No-Go",
  "Approved for Launch", "Not Approved for Launch", "Launched", "Hypercare",
];

const SECTION_STATUS = ["Not Started", "In Progress", "Complete", "At Risk", "Blocked"];

const STATUS_COLORS = {
  "Planning": "bg-slate-100 text-slate-600",
  "In Progress": "bg-blue-100 text-blue-700",
  "At Risk": "bg-amber-100 text-amber-700",
  "Blocked": "bg-red-100 text-red-700",
  "Ready for Go/No-Go": "bg-purple-100 text-purple-700",
  "Approved for Launch": "bg-emerald-100 text-emerald-700",
  "Not Approved for Launch": "bg-red-100 text-red-700",
  "Launched": "bg-teal-100 text-teal-700",
  "Hypercare": "bg-orange-100 text-orange-700",
  "Not Started": "bg-slate-100 text-slate-500",
  "Complete": "bg-emerald-100 text-emerald-700",
};

const SECTION_ICONS = {
  "Requirements & Scope Sign-Off": Target,
  "Legal & Compliance Readiness": Scale,
  "Security & Application Security Readiness": Shield,
  "Marketing Readiness": Megaphone,
  "Enrollment / Business UAT": FlaskConical,
  "Defect & Risk Readiness": AlertTriangle,
  "Operational Readiness": Settings2,
  "Leadership Readiness Presentation": Presentation,
  "Go / No-Go Review": CheckCircle2,
  "Formal Sign-Off": FileSignature,
  "Production Launch": Zap,
  "Hypercare": HeartPulse,
};

function getIcon(name) {
  return SECTION_ICONS[name] || CircleDot;
}

function computeStats(plan) {
  if (!plan || !plan.sections) return { pct: 0, total: 0, checked: 0, blockers: 0, approvals: 0, approvalsNeeded: 0 };
  let total = 0, checked = 0, blockers = 0, approvals = 0, approvalsNeeded = 0;
  for (const s of plan.sections) {
    for (const it of (s.items || [])) { total++; if (it.checked) checked++; }
    if (s.status === "Blocked") blockers++;
    if (s.is_launch_gate) { approvalsNeeded++; if (s.status === "Complete") approvals++; }
  }
  return { pct: total > 0 ? Math.round((checked / total) * 100) : 0, total, checked, blockers, approvals, approvalsNeeded };
}

function daysUntilLaunch(dateStr) {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  return diff;
}

/* ─── Section Component ─── */
const GTMSection = ({ section, index, totalSections, onUpdate, onDeleteSection, onAddItem, onDeleteItem, onMoveSection }) => {
  const [collapsed, setCollapsed] = useState(section.collapsed !== false);
  const Icon = getIcon(section.name);
  const totalItems = (section.items || []).length;
  const checkedItems = (section.items || []).filter(i => i.checked).length;
  const pct = totalItems > 0 ? Math.round((checkedItems / totalItems) * 100) : 0;
  const [newItemText, setNewItemText] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(section.name);

  const toggleItem = (itemId) => {
    const updated = (section.items || []).map(i => i.id === itemId ? { ...i, checked: !i.checked } : i);
    onUpdate(section.id, { items: updated });
  };

  const updateItemField = (itemId, field, value) => {
    const updated = (section.items || []).map(i => i.id === itemId ? { ...i, [field]: value } : i);
    onUpdate(section.id, { items: updated });
  };

  const statusColor = STATUS_COLORS[section.status] || STATUS_COLORS["Not Started"];

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`gtm-section-${section.id}`}>
      {/* Section Header */}
      <div
        className="flex items-center gap-3 px-5 py-3.5 cursor-pointer transition-colors hover:bg-slate-50"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex flex-col gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => onMoveSection(index, -1)}
            disabled={index === 0}
            className={`p-0.5 rounded ${index === 0 ? "text-slate-200 cursor-default" : "text-slate-400 hover:text-slate-700 hover:bg-slate-100"}`}
            title="Move up" data-testid={`gtm-section-up-${section.id}`}
          ><ArrowUp className="w-3.5 h-3.5" /></button>
          <button
            onClick={() => onMoveSection(index, 1)}
            disabled={index === totalSections - 1}
            className={`p-0.5 rounded ${index === totalSections - 1 ? "text-slate-200 cursor-default" : "text-slate-400 hover:text-slate-700 hover:bg-slate-100"}`}
            title="Move down" data-testid={`gtm-section-down-${section.id}`}
          ><ArrowDown className="w-3.5 h-3.5" /></button>
        </div>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-slate-100 text-slate-600">
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {editingName ? (
              <Input
                value={nameVal} onChange={(e) => setNameVal(e.target.value)}
                onBlur={() => { onUpdate(section.id, { name: nameVal }); setEditingName(false); }}
                onKeyDown={(e) => { if (e.key === "Enter") { onUpdate(section.id, { name: nameVal }); setEditingName(false); } }}
                className="h-7 text-sm font-semibold w-64" autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <h3 className="text-sm font-semibold text-slate-800 truncate" onDoubleClick={(e) => { e.stopPropagation(); setEditingName(true); }}>
                {section.name}
              </h3>
            )}
            {section.is_launch_gate && (
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-600 font-bold uppercase tracking-wider flex-shrink-0">
                Launch Gate
              </span>
            )}
          </div>
          <p className="text-[11px] text-slate-400 truncate">{section.description}</p>
        </div>
        {/* Progress */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="w-24 h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-xs text-slate-500 w-8 text-right">{pct}%</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColor}`}>{section.status}</span>
          {collapsed ? <ChevronRight className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </div>

      {/* Section Body */}
      {!collapsed && (
        <div className="px-5 pb-4 border-t border-slate-100">
          {/* Section Meta */}
          <div className="flex flex-wrap gap-3 py-3 border-b border-slate-100 mb-3">
            <div className="flex items-center gap-1.5">
              <label className="text-[10px] text-slate-400 uppercase tracking-wider">Status</label>
              <Select value={section.status} onValueChange={(v) => onUpdate(section.id, { status: v })}>
                <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{SECTION_STATUS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1.5">
              <label className="text-[10px] text-slate-400 uppercase tracking-wider">Owner</label>
              <Input value={section.owner || ""} onChange={(e) => onUpdate(section.id, { owner: e.target.value })} className="h-7 w-36 text-xs" placeholder="Assign owner" />
            </div>
            <div className="flex items-center gap-1.5">
              <label className="text-[10px] text-slate-400 uppercase tracking-wider">Due</label>
              <Input type="date" value={section.due_date || ""} onChange={(e) => onUpdate(section.id, { due_date: e.target.value })} className="h-7 w-36 text-xs" />
            </div>
            <div className="flex items-center gap-1.5">
              <label className="text-[10px] text-slate-400 uppercase tracking-wider">Dept</label>
              <Input value={section.department || ""} onChange={(e) => onUpdate(section.id, { department: e.target.value })} className="h-7 w-28 text-xs" placeholder="Department" />
            </div>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <Checkbox checked={section.is_launch_gate} onCheckedChange={(v) => onUpdate(section.id, { is_launch_gate: v, required_for_launch: v, blocks_launch_if_red: v })} />
              <span className="text-[10px] text-slate-500">Launch Gate</span>
            </label>
          </div>

          {/* Purpose */}
          {section.purpose && (
            <p className="text-xs text-slate-500 italic mb-3 bg-slate-50 rounded-lg px-3 py-2">{section.purpose}</p>
          )}

          {/* Approvers */}
          {(section.approvers || []).length > 0 && (
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-3 h-3 text-slate-400" />
              <span className="text-[10px] text-slate-400 uppercase tracking-wider">Required Approvals:</span>
              <div className="flex flex-wrap gap-1">
                {section.approvers.map((a, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{a}</span>
                ))}
              </div>
            </div>
          )}

          {/* Checklist Items */}
          <div className="space-y-1">
            {(section.items || []).map((item) => (
              <div key={item.id} className="flex items-start gap-2 group py-1.5 px-2 rounded-lg hover:bg-slate-50 transition-colors">
                <Checkbox
                  checked={item.checked}
                  onCheckedChange={() => toggleItem(item.id)}
                  className="mt-0.5"
                  data-testid={`gtm-check-${item.id}`}
                />
                <div className="flex-1 min-w-0">
                  <span className={`text-xs ${item.checked ? "text-slate-400 line-through" : "text-slate-700"}`}>
                    {item.text}
                  </span>
                </div>
                <Input
                  value={item.owner || ""} onChange={(e) => updateItemField(item.id, "owner", e.target.value)}
                  className="h-6 w-28 text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                  placeholder="Owner"
                />
                <button
                  onClick={() => setDeleteTarget({ id: item.id, label: item.text })}
                  className="p-0.5 rounded text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>

          {/* Add Item */}
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-dashed border-slate-200">
            <Plus className="w-3 h-3 text-slate-400" />
            <Input
              value={newItemText} onChange={(e) => setNewItemText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && newItemText.trim()) { onAddItem(section.id, newItemText.trim()); setNewItemText(""); } }}
              placeholder="Add requirement..."
              className="h-7 text-xs flex-1"
              data-testid={`gtm-add-item-${section.id}`}
            />
            <Button size="sm" variant="ghost" className="h-7 text-xs"
              onClick={() => { if (newItemText.trim()) { onAddItem(section.id, newItemText.trim()); setNewItemText(""); } }}>
              Add
            </Button>
          </div>

          {/* Delete Section */}
          <div className="flex justify-end mt-3 pt-2 border-t border-slate-100">
            <Button variant="ghost" size="sm" className="h-7 text-xs text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => onDeleteSection(section.id)}>
              <Trash2 className="w-3 h-3 mr-1" /> Remove Section
            </Button>
          </div>

          <ConfirmDeleteDialog
            open={!!deleteTarget} onClose={() => setDeleteTarget(null)}
            onConfirm={() => { onDeleteItem(section.id, deleteTarget?.id); setDeleteTarget(null); }}
            title="Remove Checklist Item" description={`Remove "${deleteTarget?.label}"?`}
          />
        </div>
      )}
    </div>
  );
};

/* ─── Main Page ─── */
export default function GoToMarketPage() {
  const { selectedProduct } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef(null);

  const productId = selectedProduct?.id;

  const fetchPlan = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/gtm/${productId}`);
      setPlan(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [productId]);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  const autoSave = useCallback((updatedPlan) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await axios.patch(`${API}/gtm/${productId}`, {
          launch_date: updatedPlan.launch_date,
          product_owner: updatedPlan.product_owner,
          executive_sponsor: updatedPlan.executive_sponsor,
          overall_status: updatedPlan.overall_status,
          sections: updatedPlan.sections,
        });
      } catch (e) { console.error(e); }
      setSaving(false);
    }, 800);
  }, [productId]);

  const updatePlan = (field, value) => {
    setPlan(prev => {
      const updated = { ...prev, [field]: value };
      autoSave(updated);
      return updated;
    });
  };

  const updateSection = (sectionId, fields) => {
    setPlan(prev => {
      const updated = {
        ...prev,
        sections: prev.sections.map(s => s.id === sectionId ? { ...s, ...fields } : s),
      };
      autoSave(updated);
      return updated;
    });
  };

  const addItem = (sectionId, text) => {
    setPlan(prev => {
      const updated = {
        ...prev,
        sections: prev.sections.map(s =>
          s.id === sectionId
            ? { ...s, items: [...(s.items || []), { id: crypto.randomUUID(), text, checked: false, mandatory: true, owner: "", notes: "" }] }
            : s
        ),
      };
      autoSave(updated);
      return updated;
    });
  };

  const deleteItem = (sectionId, itemId) => {
    setPlan(prev => {
      const updated = {
        ...prev,
        sections: prev.sections.map(s =>
          s.id === sectionId ? { ...s, items: (s.items || []).filter(i => i.id !== itemId) } : s
        ),
      };
      autoSave(updated);
      return updated;
    });
  };

  const addSection = async () => {
    try {
      const r = await axios.post(`${API}/gtm/${productId}/sections`, { name: "New Section", order: (plan.sections || []).length });
      setPlan(prev => ({ ...prev, sections: [...(prev.sections || []), r.data] }));
    } catch (e) { console.error(e); }
  };

  const deleteSection = async (sectionId) => {
    try {
      await axios.delete(`${API}/gtm/${productId}/sections/${sectionId}`);
      setPlan(prev => ({ ...prev, sections: (prev.sections || []).filter(s => s.id !== sectionId) }));
    } catch (e) { console.error(e); }
  };

  const moveSection = (fromIndex, direction) => {
    setPlan(prev => {
      const sorted = [...(prev.sections || [])].sort((a, b) => a.order - b.order);
      const toIndex = fromIndex + direction;
      if (toIndex < 0 || toIndex >= sorted.length) return prev;
      const item = sorted[fromIndex];
      sorted.splice(fromIndex, 1);
      sorted.splice(toIndex, 0, item);
      const reordered = sorted.map((s, i) => ({ ...s, order: i }));
      const updated = { ...prev, sections: reordered };
      autoSave(updated);
      return updated;
    });
  };

  if (!productId) return <div className="p-8 text-center text-slate-400">Select a product to view the Go-to-Market Plan</div>;
  if (loading) return <div className="flex items-center justify-center p-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!plan) return <div className="p-8 text-center text-slate-400">Could not load plan</div>;

  const stats = computeStats(plan);
  const daysLeft = daysUntilLaunch(plan.launch_date);

  return (
    <div className="max-w-6xl mx-auto pb-12" data-testid="gtm-page">
      {/* ── Header ── */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
            <Rocket className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 tracking-tight">Go-to-Market Plan</h1>
            <p className="text-xs text-slate-400">{selectedProduct?.name} — Launch Readiness Command Center</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {saving && <span className="flex items-center gap-1 text-xs text-slate-400"><Loader2 className="w-3 h-3 animate-spin" /> Saving...</span>}
          </div>
        </div>

        {/* Key Metrics Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
          <Card className="border-slate-200/80">
            <CardContent className="p-3">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Launch Date</p>
              <Input type="date" value={plan.launch_date || ""} onChange={(e) => updatePlan("launch_date", e.target.value)} className="h-7 text-xs" data-testid="gtm-launch-date" />
            </CardContent>
          </Card>
          <Card className="border-slate-200/80">
            <CardContent className="p-3">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Product Owner</p>
              <Input value={plan.product_owner || ""} onChange={(e) => updatePlan("product_owner", e.target.value)} className="h-7 text-xs" placeholder="Assign" data-testid="gtm-product-owner" />
            </CardContent>
          </Card>
          <Card className="border-slate-200/80">
            <CardContent className="p-3">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Exec Sponsor</p>
              <Input value={plan.executive_sponsor || ""} onChange={(e) => updatePlan("executive_sponsor", e.target.value)} className="h-7 text-xs" placeholder="Assign" data-testid="gtm-exec-sponsor" />
            </CardContent>
          </Card>
          <Card className="border-slate-200/80">
            <CardContent className="p-3">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Status</p>
              <Select value={plan.overall_status} onValueChange={(v) => updatePlan("overall_status", v)}>
                <SelectTrigger className="h-7 text-xs" data-testid="gtm-overall-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </CardContent>
          </Card>
          <Card className="border-slate-200/80">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Completion</p>
              <p className="text-lg font-bold text-slate-800">{stats.pct}%</p>
            </CardContent>
          </Card>
          <Card className={`border-slate-200/80 ${stats.blockers > 0 ? "bg-red-50/50 border-red-200" : ""}`}>
            <CardContent className="p-3 text-center">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Blockers</p>
              <p className={`text-lg font-bold ${stats.blockers > 0 ? "text-red-600" : "text-slate-800"}`}>{stats.blockers}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200/80">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Approvals</p>
              <p className="text-lg font-bold text-slate-800">{stats.approvals}<span className="text-xs text-slate-400 font-normal">/{stats.approvalsNeeded}</span></p>
            </CardContent>
          </Card>
        </div>

        {/* Progress Bar + Days */}
        <div className="flex items-center gap-4">
          <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${stats.pct >= 100 ? "bg-emerald-500" : stats.pct >= 60 ? "bg-blue-500" : "bg-amber-500"}`}
              style={{ width: `${stats.pct}%` }}
            />
          </div>
          <span className="text-xs text-slate-500">{stats.checked}/{stats.total} items</span>
          {daysLeft !== null && (
            <span className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${daysLeft <= 7 ? "bg-red-100 text-red-700" : daysLeft <= 30 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
              <Clock className="w-3 h-3" />
              {daysLeft > 0 ? `${daysLeft}d to launch` : daysLeft === 0 ? "Launch day!" : `${Math.abs(daysLeft)}d overdue`}
            </span>
          )}
        </div>
      </div>

      {/* ── Sections ── */}
      <div className="space-y-3">
        {(plan.sections || []).sort((a, b) => a.order - b.order).map((section, idx, arr) => (
          <GTMSection
            key={section.id} section={section} index={idx} totalSections={arr.length}
            onUpdate={updateSection}
            onDeleteSection={deleteSection}
            onAddItem={addItem}
            onDeleteItem={deleteItem}
            onMoveSection={moveSection}
          />
        ))}
      </div>

      {/* Add Section */}
      <div className="mt-4 flex justify-center">
        <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={addSection} data-testid="gtm-add-section">
          <Plus className="w-3 h-3" /> Add Section
        </Button>
      </div>
    </div>
  );
}
